import React, { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Container as ContainerBase } from "components/misc/Layouts";
import { CallApiFetchAuthorized, CallApiPutAuthorized, CallApiDeleteAuthorized } from "helpers/CallApiFetch";
import tw from "twin.macro";
import styled from "styled-components";
import AdminHeader from 'pages/Admin/Header.js';
import Select from 'react-select';
import ToggleSwitch from 'components/misc/ToggleSwitch.js';

const Container = tw(ContainerBase)`min-h-screen bg-primary-900 text-white font-medium flex justify-center`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-1 sm:my-8 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`p-6 sm:p-12`;
const MainContent = tw.div`mt-4 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold items-center flex`;

const Form = tw.form`mx-auto max-w-xs pt-8`;
const Input = tw.input`w-full px-6 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 text-sm mt-5`;
const HalfInput = tw(Input)`w-1/2`;
const TextArea = tw.textarea`w-full px-6 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 text-sm mt-5`;

const SubmitButton = tw.button`mt-5 font-semibold bg-primary-500 text-gray-100 w-full py-4 rounded-lg`;
const DeleteButton = tw.button`mt-3 font-semibold bg-red-500 text-white w-full py-4 rounded-lg hover:bg-red-700`;

const Error = tw.p`font-medium bg-red-300 text-center p-4 mt-8`;

const ExpirationRow = tw.div`flex gap-2 mt-5`;

const StyledSelect = styled(Select)`
  ${tw`mt-5 w-1/2`}
`;

function DiscountEdit({
  a14n = null,
  op = 'create',
  heading = 'Создать скидку',
  buttonLabel = 'Создать'
}) {
  const params = useParams();

  const [error, setError] = useState(false);
  const [redirect, setRedirect] = useState(false);

  const [discount, setDiscount] = useState({
    name: '',
    percent: 0,
    is_permanent: false,
    usages_total: 0,
    description: '',
    expiration_seconds: null,
  });

  const [expirationValue, setExpirationValue] = useState('');

  const units = {
    days: 86400,
    weeks: 604800,
    months: 2592000,
    years: 31536000,
  };

  useEffect(() => {
    if (op === 'edit') {
      CallApiFetchAuthorized('/api/discounts/' + params.id, null, a14n.token)
        .then(result => {
          if (result.success) {
            setDiscount(result.data);

            if (result.data.expiration_seconds) {
              const seconds = result.data.expiration_seconds;
              if (seconds % units.years === 0) {
                setExpirationUnit('years');
                setExpirationValue(seconds / units.years);
              } else if (seconds % units.months === 0) {
                setExpirationUnit('months');
                setExpirationValue(seconds / units.months);
              } else if (seconds % units.weeks === 0) {
                setExpirationUnit('weeks');
                setExpirationValue(seconds / units.weeks);
              } else {
                setExpirationUnit('days');
                setExpirationValue(seconds / units.days);
              }
            }
          } else {
            setError(result.message);
          }
        });
    }
    // eslint-disable-next-line
  }, [a14n.token]);

  const expirationOptions = [
    { value: 'days', label: 'Дни' },
    { value: 'weeks', label: 'Недели' },
    { value: 'months', label: 'Месяцы' },
    { value: 'years', label: 'Года' },
  ];

  const [expirationUnit, setExpirationUnit] = useState(expirationOptions[0]);

  function handleExpirationChange(value, unitObj) {
    setExpirationValue(value);
    setExpirationUnit(unitObj);

    if (!value) {
      setDiscount(prev => ({ ...prev, expiration_seconds: null }));
      return;
    }

    const seconds = value * units[unitObj.value];
    setDiscount(prev => ({ ...prev, expiration_seconds: seconds }));
  }

  function SubmitHandler(event) {
    event.preventDefault();

    if (op === 'create') {
      CallApiFetchAuthorized('/api/discounts', discount, a14n.token)
        .then(result => {
          if (result.success) {
            setRedirect('/admin/discounts');
          } else {
            setError(result.message);
          }
        });
    } else {
      CallApiPutAuthorized('/api/discounts/' + params.id, discount, a14n.token)
        .then(result => {
          if (result.success) {
            setRedirect('/admin/discounts');
          } else {
            setError(result.message);
          }
        });
    }
  }

  function DeleteHandler() {
    if (!window.confirm('Удалить скидку?')) return;

    CallApiDeleteAuthorized('/api/discounts/' + params.id, a14n.token)
      .then(result => {
        if (result.success || result === undefined) {
          setRedirect('/admin/discounts');
        } else {
          setError(result.message || 'Ошибка удаления');
        }
      });
  }

  return (
    <>
      <AdminHeader a14n={a14n} />
      <Container>
        <Content>
          <MainContainer>
            <MainContent>

              { redirect && <Navigate to={redirect} />}
              <Heading>{heading}</Heading>

              <Form onSubmit={SubmitHandler}>
                <Input
                  placeholder="Название"
                  value={discount.name}
                  onChange={e => setDiscount(prev => ({ ...prev, name: e.target.value }))}
                  required
                />

                <br /><br />
                <label>Процент скидки:</label>
                <Input
                  type="number"
                  placeholder="Процент"
                  min="0"
                  max="100"
                  value={discount.percent}
                  onChange={e => setDiscount(prev => ({ ...prev, percent: e.target.value }))}
                  required
                />

                <TextArea
                  placeholder="Описание"
                  value={discount.description || ''}
                  onChange={e => setDiscount(prev => ({ ...prev, description: e.target.value }))}
                />

                <br /><br />
                <hr />
                <br />
                <ToggleSwitch
                  checked={discount.is_permanent}
                  onChange={e =>
                    setDiscount(prev => ({
                      ...prev,
                      is_permanent: e.target.checked
                    }))
                  }
                  label="Постоянная"
                />

                <br /><br />
                <label>Ограничение использований:</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Всего использований"
                  value={discount.usages_total}
                  onChange={e => setDiscount(prev => ({ ...prev, usages_total: e.target.value }))}
                />

                <br /><br />
                <label>Ограничение по сроку действия:</label>
                <ExpirationRow>
                  <HalfInput
                    type="number"
                    placeholder="Срок"
                    value={expirationValue}
                    onChange={e => handleExpirationChange(e.target.value, expirationUnit)}
                  />

                  <StyledSelect
                    options={expirationOptions}
                    value={expirationUnit}
                    className="w-1/2"
                    onChange={(option) => handleExpirationChange(expirationValue, option)}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'rgb(237 242 247)',
                        minHeight: '54px'
                      }),
                    }}
                  />
                </ExpirationRow>

                <SubmitButton>{buttonLabel}</SubmitButton>

                {op === 'edit' && (
                  <DeleteButton type="button" onClick={DeleteHandler}>
                    Удалить
                  </DeleteButton>
                )}

                {error && <Error>{error}</Error>}
              </Form>

            </MainContent>
          </MainContainer>
        </Content>
      </Container>
    </>
  );
}

export function CreateDiscount(params) {
  return DiscountEdit(params);
}

export function EditDiscount(params) {
  return DiscountEdit({
    ...params,
    op: 'edit',
    heading: 'Изменить скидку',
    buttonLabel: 'Сохранить',
  });
}
